import onReady from '../utils/onReady';
import { addEvent } from '../utils/events';
import * as ClassList from '../utils/classList';
import triggerCustomEvent from '../utils/triggerCustomEvent';


/**
 * Boilerplate class demonstrating basic construction.
 * You can expand from here, or find verbose examples in example.js.
 *
 * @param {HTMLElement} container
 * @constructor
 */
class SearchSuggestions {

    /**
     * The constructor is fired once the class is instantiated.
     *
     * @param {HTMLElement} container
     */
    constructor(form) {
        // Run initializing code once the DOM is ready.
        onReady(() => this.init(form));

        // You can put other initializer-code here if it isn't dependent on the DOM in any way.
    }


    getData(value) {
        fetch(this.settings.url + value, {
            method: 'get',
            headers: new Headers({
                'Accept': 'application/json'
            })
        })
            .then(this.handleResponse.bind(this))
            .then(data => this.addContent(data))
            .catch(error => window.console.log("Catch: " + error));
    }

    handleResponse(response) {
        let contentType = response.headers.get('content-type');
        if (contentType.includes('application/json')) {
            return this.handleJSONResponse(response);
        } else if (contentType.includes('text/html')) {
            return this.handleTextResponse(response);
        } else {
            throw new Error(`Sorry, content-type ${contentType} not supported`);
        }
    }

    handleJSONResponse(response) {
        return response.json()
            .then(json => {
                if (response.ok) {
                    return json;
                } else {
                    return Promise.reject(Object.assign({}, json, {
                        status: response.status,
                        statusText: response.statusText
                    }));
                }
            });
    }

    handleTextResponse(response) {
        return response.text()
            .then(text => {
                if (response.ok) {
                    return text;
                } else {
                    return Promise.reject({
                        status: response.status,
                        statusText: response.statusText,
                        err: text
                    });
                }
            });
    }

    createTypeAheadListitem(data) {
        // http://wesbos.com/template-strings-html
        let temp = document.createElement('div'),
            listitem,
            tempString = `<li>
                <a href="${data.Link}">
                    ${data.ShowName}
                </a>
            </li>
            `;

        // trick to change the string to a html element to return
        temp.innerHTML = tempString;
        listitem = temp.firstChild;

        return listitem;
    }

    addContent(data) {
        // Remove spinner, to indicate search is done
        triggerCustomEvent(this.dom.form, "fetchComplete");

        // Delete <ul> from DOM, and make a new virtual <ul>
        this.deleteDropdown();
        this.makeDropdown();

        // If data is present, create <li>'s
        if (data.length) {
            let counter = 0;

            for (let i = 0; i < data.length; i += 1) {
                let listitem = this.createTypeAheadListitem(data[i]);

                // Append <li>'s to our virtual <ul>
                this.dom.dropdownList.appendChild(listitem);
                counter += 1;
            }

            // Add our <ul> to the DOM
            this.dom.form.appendChild(this.dom.dropdownList);
        }
    }

    showDropdown() {
        ClassList.remove(this.dom.dropdownList, "hidden");
    }

    hideDropdown() {
        ClassList.add(this.dom.dropdownList, "hidden");
    }

    emptyDropdown() {
        this.dom.dropdownList.innerHTML = ""; // Empty the ul
    }

    deleteDropdown() {
        if (this.dom.form.contains(this.dom.dropdownList)) {
            this.dom.dropdownList.parentNode.removeChild(this.dom.dropdownList);
        }
    }

    makeDropdown() {
        this.settings.chosen = "";
        this.dom.dropdownList = document.createElement('ul');
        ClassList.add(this.dom.dropdownList, "search-suggestion");
    }

    onBlur() {
        // Timeout so clicks on the list are registered before the list is hidden
        window.setTimeout(() => {
            // ClassList.remove(this.dom.container, "waiting");
            this.hideDropdown();
        },500);
    }

    onFocus() {
        this.showDropdown();
        // this.onKeyup({ keyCode: 8 }); // nca: fake [Backspace] event, as quickfix to show dropdown.
    }


    onKeyup(event) {
        let keycode = event.keyCode;
        let value = "";

        // keycode 27 = esc
        if (keycode === 27) {
            this.dom.input.blur();
            return;
        }

        // keycode 46 = delete
        // keycode 8 = backspace
        // we need these to keep searching when deleting characters
        if ((keycode < 32 && keycode !== 8) || (keycode >= 33 && keycode <= 45) || (keycode >= 112 && keycode <= 123)) {
            // ignore
            return;
        } else {
            if (event.target.value.length > 2) {
                value = encodeURIComponent(event.target.value);

                // Add spinner, to indicate search is happening
                triggerCustomEvent(this.dom.form, "submit");

                this.getData(value);
            } else {
                if (this.dom.dropdownList.innerHTML !== "") {
                    this.emptyDropdown();
                }
            }
        }
    }

    onKeydown(event) {
        if (this.settings.clickedFlag) {
            return;
        }

        let keycode = event.keyCode,
            value = this.dom.input.value,
            listItems,
            activeListItem;

        // keycode 13 = enter
        // keycode 32 = spacebar
        if ([13].includes(keycode) && this.settings.chosen !== "") {

            listItems = this.dom.dropdownList.querySelectorAll("li");


            var link = listItems[this.settings.chosen] && listItems[this.settings.chosen].querySelector('a');
            if (link) {
                // Add visual indication of click
                activeListItem = listItems[this.settings.chosen];
                ClassList.add(activeListItem, "is-clicked");
                ClassList.add(this.dom.form, "is-clicked");
                this.settings.clickedFlag = true;

                // Trigger submit event, to trigger submitLoader
                triggerCustomEvent(this.dom.form, "submit");
                link.click();
            }

            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        if (value.length > 2) {
            // keycode 38 = up arrow
            // keycode 40 = down arrow
            if ([38,40].includes(keycode)) {
                event.preventDefault();

                // get all items from the dropdown list
                listItems = this.dom.dropdownList.querySelectorAll("li");
                // and remove the active class
                [].forEach.call(listItems, function(item) {
                    ClassList.remove(item, "is-active");
                });
                //console && console.log("set chosen");
                // get the active listitem when using up/down arrow keys
                if (keycode === 38) {
                    if (this.settings.chosen === "") {
                        this.settings.chosen = 0;
                    } else if ( this.settings.chosen > 0 ) {
                        this.settings.chosen--;
                    }
                } else if (keycode === 40) {
                    if (this.settings.chosen === "") {
                        this.settings.chosen = 0;
                    } else if ( (this.settings.chosen+1) < listItems.length ) {
                        this.settings.chosen++;
                    }
                }
                // cache active item
                activeListItem = listItems[this.settings.chosen];
                // and highlight it
                ClassList.add(activeListItem, "is-active");
            }
        }
    }



    /**
     * The actual initialization function, fired once the DOM is ready.
     *
     * @param {HTMLElement} container
     */
    init(form) {

        this.dom = {
            form,
            input: form.querySelector(".main-search__input"),
            submit: form.querySelector(".main-search__submit"),
            dropdownList: document.createElement('ul')
        };

        this.settings = {
            url: window.location.protocol + "//" + window.location.hostname + "/api/search/sections?searchText=",
            chosen: "",
            clickedFlag: false
        };

        // Adding events
        if (this.dom.input) {
            addEvent(this.dom.input, "focus", () => this.onFocus());
            addEvent(this.dom.input, "blur", () => this.onBlur());
            addEvent(this.dom.input, "keyup", event => this.onKeyup(event));
            addEvent(this.dom.input, "keydown", event => this.onKeydown(event));
            addEvent(this.dom.form, "submit", event => {event.preventDefault()});
        }
    }

}


export default SearchSuggestions;
